import React, { useEffect, useRef, useState } from 'react';
import {
    Button,
    Grid,
    TextField,
    Typography,
    Paper,
    Divider,
    Stack,
    Box,
    Container,
    Chip
} from '@mui/material';
import {
    Download as DownloadIcon,
    QrCode as QrIcon,
    RocketLaunch as RocketIcon,
    InfoOutlined as InfoIcon
} from '@mui/icons-material';

/**
 * --- INTERNAL MOCK IMPLEMENTATIONS ---
 */

// Mock for IntegrationTab
const IntegrationTab = () => (
    <Box sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px dashed #ccc' }}>
        <Typography variant="caption" color="text.secondary">Integration Tab Placeholder</Typography>
    </Box>
);

// Mock for Hooks/Auth
const useAuth = () => ({ Logout: (code) => console.log("Logout triggered:", code) });
const getOnlineInfo = async () => ({ code: 0, data: { title: 'MiiRA Store', description: 'Premium POS Experience', socialLinks: {} } });
const updateOnlineInfo = async (data) => ({ code: 0, data });

/**
 * Scannable QRCodeCanvas Component
 * Uses a public API to generate a real, scannable pattern.
 */
const QRCodeCanvas = ({ value, size, id }) => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            const img = new Image();
            img.crossOrigin = "anonymous"; 
            img.src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(value)}&margin=10`;

            img.onload = () => {
                ctx.clearRect(0, 0, size, size);
                ctx.drawImage(img, 0, 0, size, size);
            };
            ctx.fillStyle = '#fafafa';
            ctx.fillRect(0, 0, size, size);
        }
    }, [size, value]);

    return (
        <canvas
            ref={canvasRef}
            id={id}
            width={size}
            height={size}
            style={{
                width: size,
                height: size,
                border: '6px solid #1a1b2f',
                borderRadius: '16px',
                backgroundColor: '#fff',
                display: 'block',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
            }}
        />
    );
};

export const Online = () => {
    const storeID = useRef(() => {
        try {
            const ds = window.localStorage.getItem('ds');
            return ds ? JSON.parse(ds) : "demo_store";
        } catch (e) { return "demo_store"; }
    });

    const { Logout } = useAuth();
    const [onlineInfo, setOnlineInfo] = useState({
        storeID: storeID.current(),
        title: '',
        description: '',
        socialLinks: { facebook: '', instagram: '', youtube: '', twitter: '', linkedin: '' }
    });

    const storeMenuUrl = `https://stores.miirapos.com/${onlineInfo.storeID}/menu`;

    const handleDownloadQR = () => {
        const canvas = document.getElementById("store-qr-canvas");
        if (!canvas) return;
        try {
            const url = canvas.toDataURL("image/png");
            const link = document.createElement("a");
            link.href = url;
            link.download = `${onlineInfo.title || 'MiiRA-Store'}-Menu-QR.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Download failed:", err);
        }
    };

    useEffect(() => {
        const fetcher = async () => {
            const res = await getOnlineInfo();
            if (res.code === 0) setOnlineInfo(prev => ({ ...prev, ...res.data }));
        };
        fetcher();
    }, []);

    return (
        <Container maxWidth={false} sx={{ width: '100%', pb: 5 }}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h5" fontWeight="900" color="primary" gutterBottom>
                    Digital Presence
                </Typography>
                <IntegrationTab />
            </Box>

            <Box sx={{ position: 'relative', borderRadius: 4, }}>
                {/* SECTION 1: THE BACKGROUND (Website Settings) 
                    This is blurred to show "Coming Soon"
                */}
                <Box sx={{ filter: 'blur(3px)', opacity: 0.4, pointerEvents: 'none', userSelect: 'none' }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>Website SEO & Info</Typography>
                                <Divider sx={{ mb: 3 }} />
                                <Stack spacing={2}>
                                    <TextField fullWidth label="SEO Title" size="small" />
                                    <TextField fullWidth label="Meta Description" multiline rows={2} size="small" />
                                    <TextField fullWidth label="Banner Heading" size="small" />
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </Box>

                {/* SECTION 2: THE OVERLAY LAYER
                    Contains the functional QR Module on the right and the "Coming Soon" notice on the left.
                */}
                <Box sx={{
                    position: 'absolute', inset: 0, zIndex: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(255, 255, 255, 0.4)', backdropFilter: 'blur(1px)'
                }}>
                    <Container maxWidth="lg">
                        <Grid container spacing={4} alignItems="stretch">
                            {/* Left: Coming Soon Message */}
                            <Grid item xs={12} md={7} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Paper elevation={0} sx={{ p: 4, bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 5, border: '1px solid #eee', boxShadow: '0 20px 40px rgba(0,0,0,0.05)' }}>
                                    <Chip 
                                        icon={<RocketIcon />} 
                                        label="Beta Feature" 
                                        color="primary" 
                                        sx={{ mb: 2, fontWeight: 'bold' }} 
                                    />
                                    <Typography variant="h3" fontWeight="900" color="text.primary" gutterBottom>
                                        Custom Website <br /> 
                                        <span style={{ color: 'tomato' }}>Builder</span>
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '1.1rem', maxWidth: '450px' }}>
                                        We are currently finalizing the self-hosted website module. Soon you'll be able to launch your own branded store with zero coding.
                                    </Typography>
                                    <Stack direction="row" spacing={2}>
                                        <Button startIcon={<InfoIcon />} color="inherit" variant="text">Learn about website builder</Button>
                                    </Stack>
                                </Paper>
                            </Grid>

                            {/* Right: Operational QR Module */}
                            <Grid item xs={12} md={5}>
                                <Paper 
                                    elevation={12} 
                                    sx={{ 
                                        p: 4, 
                                        textAlign: 'center', 
                                        borderRadius: 6, 
                                        bgcolor: '#fff',
                                        border: '2px solid',
                                        borderColor: 'primary.main',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Typography variant="subtitle2" fontWeight="900" sx={{ mb: 2, letterSpacing: 1 }}>
                                        LIVE: STORE MENU QR
                                    </Typography>
                                    
                                    <Box sx={{ mb: 3 }}>
                                        <QRCodeCanvas 
                                            id="store-qr-canvas"
                                            value={storeMenuUrl} 
                                            size={220} 
                                        />
                                    </Box>
                                    
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, px: 2 }}>
                                        Your digital menu is <strong>active</strong>. Download this QR to provide instant tableside ordering for your customers.
                                    </Typography>
                                    
                                    <Button 
                                        fullWidth 
                                        variant="contained" 
                                        startIcon={<DownloadIcon />}
                                        onClick={handleDownloadQR}
                                        sx={{ 
                                            borderRadius: 3, 
                                            py: 1.5,
                                            fontWeight: 'bold',
                                            bgcolor: '#1a1b2f',
                                            '&:hover': { bgcolor: 'tomato', transform: 'translateY(-2px)' },
                                            transition: '0.3s'
                                        }}
                                    >
                                        Download Menu QR
                                    </Button>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>
            </Box>
        </Container>
    );
};

export default Online;